/**
 * AADE Decisions Page
 *
 * View and approve/reject decisions from AADE.
 */

import React, { useState, useEffect } from 'react';
import { Decision, SyncJob } from '../../services/aadeService';
import { aadeGatewayService } from '../../services/aadeGatewayService';
import {
  CheckCircle2 as FiCheckCircle,
  XCircle as FiXCircle,
  Clock3 as FiClock,
  AlertCircle as FiAlertCircle,
} from 'lucide-react';

const AADEDecisions: React.FC = () => {
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [syncJobs, setSyncJobs] = useState<SyncJob[]>([]);
  const [selectedUploadId, setSelectedUploadId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSyncJobs();
  }, []);

  useEffect(() => {
    if (selectedUploadId) {
      fetchDecisions(selectedUploadId);
    }
  }, [selectedUploadId]);

  const fetchSyncJobs = async () => {
    try {
      const gatewayResponse = await aadeGatewayService.getSyncStatus(10);
      const completedJobs = gatewayResponse.sync_jobs.filter((job) => job.status === 'completed');
      setSyncJobs(completedJobs);

      if (completedJobs.length > 0 && !selectedUploadId) {
        setSelectedUploadId(completedJobs[0].upload_id);
      }
    } catch (err: any) {
      setError('Failed to fetch sync jobs');
    }
  };

  const fetchDecisions = async (uploadId: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await aadeGatewayService.getPendingDecisions(uploadId);
      setDecisions(response.decisions || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch decisions');
    } finally {
      setLoading(false);
    }
  };

  const handleDecision = async (
    decisionId: string,
    action: string,
    decision: Decision
  ) => {
    const notes = prompt(`Please provide notes for your decision to ${action}:`);
    if (notes === null) return; // User cancelled

    try {
      await aadeGatewayService.submitDecision({
        decision_id: decisionId,
        action,
        notes: notes || undefined,
      });

      alert(`Decision ${action}ed successfully`);

      // Remove from list
      setDecisions(decisions.filter((d) => d.decision_id !== decisionId));
    } catch (err: any) {
      alert('Failed to submit decision: ' + err.message);
    }
  };

  const getDecisionPriorityColor = (decision: Decision) => {
    const confidence = decision.confidence || 0;
    if (confidence >= 0.8) return 'border-green-500';
    if (confidence >= 0.6) return 'border-blue-500';
    if (confidence >= 0.4) return 'border-yellow-500';
    return 'border-orange-500';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">AADE Decisions</h1>
          <p className="text-gray-600 mt-2">
            Review and approve AI-powered decision recommendations
          </p>
        </div>

        {/* Upload Selector */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Data Sync
          </label>
          <select
            value={selectedUploadId}
            onChange={(e) => setSelectedUploadId(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg"
            disabled={loading}
          >
            {syncJobs.map((job) => (
              <option key={job.id} value={job.upload_id}>
                {new Date(job.completed_at || job.started_at).toLocaleString()} -{' '}
                {job.records_synced} records
              </option>
            ))}
          </select>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* Empty State */}
        {!loading && decisions.length === 0 && (
          <div className="bg-white p-12 rounded-lg shadow text-center">
            <FiCheckCircle className="mx-auto text-6xl text-green-500 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No Pending Decisions
            </h3>
            <p className="text-gray-600">
              All decisions for this data sync have been processed.
            </p>
          </div>
        )}

        {/* Decisions List */}
        {!loading && decisions.length > 0 && (
          <div className="space-y-6">
            {decisions.map((decision) => (
              <div
                key={decision.decision_id}
                className={`bg-white p-6 rounded-lg shadow-lg border-l-4 ${getDecisionPriorityColor(
                  decision
                )}`}
              >
                {/* Decision Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {decision.title}
                    </h3>
                    <p className="text-gray-600">{decision.description}</p>
                  </div>
                  <div className="ml-4">
                    <FiClock className="text-gray-400 text-2xl" />
                  </div>
                </div>

                {/* AADE Recommendation */}
                {decision.recommendation && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <FiAlertCircle className="text-blue-600" />
                      <span className="font-semibold text-blue-900">
                        AADE Recommendation
                      </span>
                      {decision.confidence && (
                        <span className="ml-auto px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                          {(decision.confidence * 100).toFixed(0)}% confidence
                        </span>
                      )}
                    </div>
                    <p className="text-blue-800">
                      Recommended action: <strong>{decision.recommendation}</strong>
                    </p>
                  </div>
                )}

                {/* Context Information */}
                {decision.context && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Context</h4>
                    <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                      {JSON.stringify(decision.context, null, 2)}
                    </pre>
                  </div>
                )}

                {/* Options */}
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Available Options</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {decision.options.map((option) => (
                      <button
                        key={option}
                        onClick={() =>
                          handleDecision(decision.decision_id, option, decision)
                        }
                        className={`p-4 border-2 rounded-lg text-left transition-all hover:shadow-md ${
                          decision.recommendation === option
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {option === 'approve' && (
                            <FiCheckCircle className="text-green-600" />
                          )}
                          {option === 'reject' && <FiXCircle className="text-red-600" />}
                          <span className="font-medium capitalize">{option}</span>
                        </div>
                        {decision.recommendation === option && (
                          <div className="text-xs text-blue-600 mt-2">✓ Recommended</div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t">
                  <button
                    onClick={() =>
                      handleDecision(decision.decision_id, 'approve', decision)
                    }
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                  >
                    ✓ Approve
                  </button>
                  <button
                    onClick={() =>
                      handleDecision(decision.decision_id, 'reject', decision)
                    }
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                  >
                    ✗ Reject
                  </button>
                  <button
                    onClick={() =>
                      handleDecision(decision.decision_id, 'defer', decision)
                    }
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                  >
                    Defer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AADEDecisions;
